import { useState } from 'react';
import type { ProductOutput } from '@st/contract';
import {
  useCheckout,
  useCustomerPortal,
  usePublicProducts,
  useSubscription,
  useTrialStatus,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { TrialBanner } from '../../components/TrialBanner';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate, formatMoney } from '../../lib/format';
import { toErrorMessage } from '../../i18n/errorMessage';
import { trialBadgeKey } from '../../lib/trial';
import { DiscountCodeField } from './DiscountCodeField';
import type { SubscriptionOutput } from '../../api/types';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  SkeletonCard,
  SkeletonList,
  StatTile,
  useToast,
  type BadgeTone,
} from '../../ui';

const STATUS_TONE: Record<SubscriptionOutput['status'], BadgeTone> = {
  active: 'success',
  past_due: 'danger',
  canceled: 'warning',
  expired: 'neutral',
  none: 'neutral',
};

const STATUS_KEY: Record<SubscriptionOutput['status'], string> = {
  active: 'subscription.statusActive',
  past_due: 'subscription.statusPastDue',
  canceled: 'subscription.statusCanceled',
  expired: 'subscription.statusExpired',
  none: 'empty.noSubscription',
};

export function Subscription() {
  const { t, locale } = useTranslation();
  const query = useSubscription();
  const portal = useCustomerPortal();
  const trial = useTrialStatus();
  const { toast } = useToast();

  async function openPortal() {
    try {
      const { url } = await portal.mutateAsync();
      // Kartica, računi i otkazivanje žive u Stripeu — ARHITEKTURA.md sekcija 6.
      window.location.assign(url);
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader title={t('subscription.title')} />

      <TrialBanner status={trial.data} className="mb-5" />

      {/* Cjenik u portalu: „Otključaj kupnjom” s ekrana treninga završava ovdje. */}
      <PlansSection paid={trial.data?.paid ?? false} />
      <QueryBoundary
        query={query}
        skeleton={<SkeletonCard />}
        isEmpty={(d) => d.status === 'none' || d.plan === null}
        empty={{
          message: t('empty.noSubscription'),
          actionLabel: t('empty.browsePlans'),
          // Paketi stoje na istom ekranu, odmah iznad. Gumb je prije bio prazan
          // klik; sada pomiče prikaz I fokus na popis, pa radi i s tipkovnicom.
          onAction: () => {
            const plans = document.getElementById('paketi');
            plans?.scrollIntoView?.({ block: 'start' });
            plans?.focus?.();
          },
        }}
      >
        {(data) => (
          <div className="flex flex-col gap-5">
            <Card>
              <CardHeader
                title={data.plan?.name ?? t('subscription.currentPlan')}
                eyebrow={t('subscription.currentPlan')}
                action={<Badge tone={STATUS_TONE[data.status]}>{t(STATUS_KEY[data.status])}</Badge>}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <StatTile label={t('subscription.currentPlan')} value={data.plan?.priceLabel ?? '—'} />
                <StatTile
                  label={t('subscription.nextPayment')}
                  value={
                    data.currentPeriodEnd
                      ? formatDate(data.currentPeriodEnd, locale)
                      : t('subscription.noNextPayment')
                  }
                />
              </div>
              {data.accessUntil && (
                <p className="mt-4 text-[13px] text-ink-muted">
                  {t('subscription.accessUntil', { date: formatDate(data.accessUntil, locale) })}
                </p>
              )}
              <div className="mt-5">
                <Button loading={portal.isPending} onClick={() => void openPortal()}>
                  {t('subscription.managePortal')}
                </Button>
                <p className="mt-2 text-[12px] text-ink-muted">
                  {t('subscription.managePortalHelp')}
                </p>
              </div>
            </Card>

            <section>
              <h2 className="eyebrow mb-3">{t('subscription.invoices')}</h2>
              {data.invoices.length === 0 ? (
                <EmptyState message={t('empty.noInvoices')} />
              ) : (
                <DataTable
                  caption={t('subscription.invoices')}
                  rows={data.invoices}
                  rowKey={(r) => r.id}
                  columns={[
                    {
                      key: 'date',
                      header: t('admin.createdAt'),
                      render: (r) => formatDate(r.issuedAt, locale),
                    },
                    {
                      key: 'amount',
                      header: t('admin.amount'),
                      align: 'right',
                      render: (r) => formatMoney(r.amountCents, r.currency, locale),
                    },
                    {
                      key: 'status',
                      header: t('subscription.status'),
                      render: () => <Badge tone="success">{t('admin.paid')}</Badge>,
                      hideOnMobile: true,
                    },
                    {
                      key: 'link',
                      header: '',
                      align: 'right',
                      /* Stripe ne izda `hosted_url` za svaki račun — bez njega nema linka. */
                      render: (r) =>
                        r.hostedUrl ? (
                          <a
                            href={r.hostedUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-accent-dark underline"
                          >
                            PDF
                          </a>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        ),
                    },
                  ]}
                />
              )}
            </section>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

/**
 * Paketi i kupnja. Checkout ide kroz `billing.checkout` — isti tok kao na javnom
 * sajtu, samo pokrenut iz portala, pa se povratne rute ne razilaze.
 */
function PlansSection({ paid }: { paid: boolean }) {
  const { t, locale } = useTranslation();
  const products = usePublicProducts(locale);
  const checkout = useCheckout();
  const { toast } = useToast();
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);

  async function buy(priceId: string, discountCode?: string): Promise<void> {
    setPendingPriceId(priceId);
    try {
      const { url } = await checkout.mutateAsync({
        priceId,
        locale,
        successPath: '/checkout/uspjeh',
        cancelPath: '/cjenik',
        ...(discountCode ? { discountCode } : {}),
      });
      window.location.assign(url);
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    } finally {
      setPendingPriceId(null);
    }
  }

  return (
    <section className="mb-6 scroll-mt-6 outline-none" id="paketi" tabIndex={-1}>
      <h2 className="eyebrow mb-3">{t('subscription.plans')}</h2>
      <QueryBoundary
        query={products}
        skeleton={<SkeletonList rows={2} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('empty.noProducts') }}
      >
        {(data) => (
          <>
            <p className="mb-3 text-[13px] text-ink-muted">{t('subscription.plansHelp')}</p>
            <ul className="grid gap-4 md:grid-cols-2">
              {data.items.map((product) => (
                <li key={product.id}>
                  <PlanCard
                    product={product}
                    paid={paid}
                    loading={pendingPriceId === product.prices[0]?.id}
                    onBuy={(priceId, discountCode) => void buy(priceId, discountCode)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </QueryBoundary>
    </section>
  );
}

function PlanCard({
  product,
  paid,
  loading,
  onBuy,
}: {
  product: ProductOutput;
  paid: boolean;
  loading: boolean;
  onBuy: (priceId: string, discountCode?: string) => void;
}) {
  const { t, locale } = useTranslation();
  const price = product.prices[0];

  /**
   * Popust se provjerava PRIJE odlaska na Stripe, na cijeni ovog paketa —
   * korisnik vidi konačan iznos dok još može odustati.
   */
  const [codeOpen, setCodeOpen] = useState(false);
  const [offCents, setOffCents] = useState<number | null>(null);
  /**
   * Kod se pamti i šalje u checkout. Prikazani iznos je samo pregled —
   * server ga svejedno ponovno provjeri i sam odluči koliko skida.
   */
  const [appliedCode, setAppliedCode] = useState<string | null>(null);

  const finalCents =
    price && offCents !== null ? Math.max(0, price.amountCents - offCents) : null;

  return (
    <Card as="article" className="flex h-full flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="font-display text-[21px] font-semibold leading-tight text-ink">
          {product.name}
        </h3>
        {!paid && product.trialWorkoutCount > 0 && (
          <Badge tone="accent">
            {t(trialBadgeKey(product.trialWorkoutCount), { n: product.trialWorkoutCount })}
          </Badge>
        )}
      </div>
      {product.description && (
        <p className="text-[14px] leading-relaxed text-ink-muted">{product.description}</p>
      )}

      {price && (
        <div className="pt-2">
          {codeOpen ? (
            <DiscountCodeField
              id={`discount-${price.id}`}
              /* Gumb „Imaš kod?" nestaje pri kliku — bez ovoga fokus padne na `<body>`. */
              autoFocus
              priceId={price.id}
              amountCents={price.amountCents}
              currency={price.currency}
              onApplied={(result) => {
                setAppliedCode(result.code);
                setOffCents(
                  result.amountOffCents ??
                    (result.kind === 'percent'
                      ? Math.round((price.amountCents * result.value) / 100)
                      : Math.min(result.value, price.amountCents)),
                );
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setCodeOpen(true)}
              className="text-[13px] text-accent-dark underline"
            >
              {t('discount.haveCode')}
            </button>
          )}
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-2">
        {price && (
          <span className="flex flex-wrap items-baseline gap-2">
            {finalCents !== null && (
              <span className="text-[14px] text-ink-muted line-through">
                {formatMoney(price.amountCents, price.currency, locale)}
              </span>
            )}
            <span className="font-display text-[21px] font-semibold text-accent-dark">
              {formatMoney(finalCents ?? price.amountCents, price.currency, locale)}
            </span>
          </span>
        )}
        {price && (
          <Button loading={loading} onClick={() => onBuy(price.id, appliedCode ?? undefined)}>
            {t('subscription.buy')}
          </Button>
        )}
      </div>
    </Card>
  );
}
